import axios from 'axios';
import { ActionButton, LinkButton } from 'types/button';
import { CompanyResponse } from 'types/company';
import { Menu } from 'types/menu';
import { EmployeesResponse, EmployeeResponse } from 'types/employee';
import { HashData, HashDataResponse } from 'types/hashData';
import { PaymentStatusResponse } from 'types/paymentStatus';
import { SendReviewResponse } from 'types/sendReviewResponse';
import { TipsType } from 'types/tips';
import { LocationResponse } from 'types/location';
import { PlaceResponse } from 'types/place';

export interface Api {
  getMenu: (shortUrl: string) => Promise<Menu>;
  getActions: (
    location: string | undefined, // todo: undefined should be removed
    company: string
  ) => Promise<ActionButton[] | LinkButton[]>;
  getCompany: (
    hash: string,
    qrType: string
  ) => Promise<CompanyResponse & { error?: string }>;
  getLocation: (
    hash: string,
    qrType: string
  ) => Promise<LocationResponse & { error?: string }>;
  getPlace: (
    hash: string,
    qrType: string
  ) => Promise<PlaceResponse & { error?: string }>;
  getEmployees: (
    company: string,
    location: string,
    orderType: string
  ) => Promise<EmployeesResponse>;
  getEmployee: (
    type: TipsType,
    hash: string
  ) => Promise<EmployeeResponse & { error?: string }>;
  getHashData: (hash: string) => Promise<HashData>; // required for CustomForm only
  getPaymentStatus: (
    type: TipsType,
    hash: string,
    uuid: string
  ) => Promise<PaymentStatusResponse>;
  launchAction: (payload: FormData) => Promise<{ success: {}; error?: string }>;
  sendReview: (payload: FormData) => Promise<SendReviewResponse>;
  redirectToMerchant: (
    link: string,
    amount: number
  ) => Promise<{
    redirect: string;
  }>;
}

export const api = (): Api => {
  const client = axios.create({ baseURL: `${API_URL}/api/v2` });

  const getMenu = (shortUrl: string) =>
    client.get<Menu>(`menu-link/${shortUrl}`).then(res => res.data);

  const getActions = (location: string | undefined, company: string) =>
    client
      .get<ActionButton[] | LinkButton[]>(`actions/${location}/${company}`)
      .then(res => res.data);

  const getCompany = (hash: string, qrType: string) => {
    const url = `company/${hash}?order_type=${qrType}`;
    return client
      .get<CompanyResponse & { error?: string }>(url)
      .then(res => res.data);
  };

  const getLocation = (hash: string, qrType: string) => {
    const url = `location/${hash}?order_type=${qrType}`;
    return client
      .get<LocationResponse & { error?: string }>(url)
      .then(res => res.data);
  };

  const getPlace = (hash: string, qrType: string) => {
    const url = `place/${hash}?order_type=${qrType}`;
    return client
      .get<PlaceResponse & { error?: string }>(url)
      .then(res => res.data);
  };

  const getEmployees = (
    company: string,
    location: string,
    orderType: string
  ) => {
    return client
      .get<EmployeesResponse & { error?: string }>(
        `tips/${company}/location/${location}/${orderType}`
      )
      .then(res => res.data);
  };

  const getEmployee = (type: TipsType, hash: string) => {
    return client
      .get<EmployeeResponse>(`tips/${type}/${hash}`)
      .then(res => res.data);
  };

  const getPaymentStatus = (type: TipsType, hash: string, uuid: string) =>
    client
      .get<PaymentStatusResponse>(`tips/${type}/${hash}/status/${uuid}`)
      .then(res => res.data);

  const getHashData = (hash: string) =>
    client.get<HashDataResponse>(`hash/${hash}`).then(res => res.data.data);

  const launchAction = (payload: FormData) =>
    client
      .post<{ success: {}; error: string }>('launch_action', payload)
      .then(res => res.data);

  const sendReview = (payload: FormData) =>
    client.post<SendReviewResponse>('r-c/', payload).then(res => res.data);

  const redirectToMerchant = (link: string, amount: number) =>
    client
      .post<{ redirect: string }>(`${link}?amount=${amount}`)
      .then(res => res.data);

  return {
    getActions,
    getCompany,
    getLocation,
    getPlace,
    getEmployees,
    getEmployee,
    getHashData,
    getPaymentStatus,
    getMenu,
    launchAction,
    sendReview,
    redirectToMerchant
  };
};
